import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, shareReplay, tap } from 'rxjs/operators';

import { environment } from '@hub-workspace/apps/logged-home/src/environments/environment';
import { ILoggedHomeAppState } from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/logged-home-app-state.interface';
import { IOneViewPermissions } from '@hub-workspace/apps/logged-home/src/app/shared/interfaces/one-view-permissions.interface';
import { UserTagAction } from '@hub-workspace/apps/logged-home/src/app/state-management/user-tag/user-tag.action';
import { isSantanderCustomer } from '@hub-workspace/apps/logged-home/src/app/state-management/user-tag/user-tag.selectors';
import { OneViewPermissionsAction } from '@hub-workspace/apps/logged-home/src/app/state-management/one-view-permissions/one-view-permissions.action';
import { UserTagState } from '@hub-workspace/apps/logged-home/src/app/state-management/user-tag/user-tag.reducer';
import { OneViewPermissionsState } from '@hub-workspace/apps/logged-home/src/app/state-management/one-view-permissions/one-view-permissions.reducer';

import { FeatureFlagsService } from '@hub-workspace/apps/logged-home/src/app/shared/services/feature-flags/feature-flags.service';

import {
  applyOneViewExperienceFlags,
  OneViewExperienceVariant,
  resolveOneViewExperienceVariant,
} from './oneview-experience-variant.util';

/**
 * Fonte única da variante da experiência OneView (alerta + tooltip do
 * Patrimônio).
 *
 * - Combina `oneViewPermissions` + `userTag` do store com as feature flags
 *   `oneviewExperienceSantanderEnabled` e `oneviewExperienceGenericEnabled`.
 *
 * Overrides para desenvolvimento (só aplicam quando `environment.allowDevOverrides` é true):
 *
 *   ?forceVariant=new|old
 *     Força a variante final diretamente, ignorando Store E flags.
 *
 *   ?mockOneView=true|false&mockSantander=true|false
 *     Mocka os inputs individuais (permissions.showWalletOneView, isSantander)
 *     e executa o pipeline real — `resolveOneViewExperienceVariant` + gate
 *     via `applyOneViewExperienceFlags`. Ou seja: as flags do Remote Config
 *     continuam valendo; só os dados do Store são substituídos.
 */
@Injectable({ providedIn: 'root' })
export class OneViewExperienceVariantService {
  public readonly variant$: Observable<OneViewExperienceVariant>;

  constructor(
    private store: Store<ILoggedHomeAppState>,
    private featureFlags: FeatureFlagsService,
  ) {
    const forcedVariant = this.readForcedVariantFromUrl();
    if (forcedVariant) {
      this.variant$ = of(forcedVariant).pipe(
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      // eslint-disable-next-line no-console
      console.warn(
        `[OneViewExperienceVariantService] Override ativo via ?forceVariant=${forcedVariant}. ` +
          'Este comportamento só é aplicado em desenvolvimento.',
      );
      return;
    }

    const santanderEnabled$ = this.featureFlags.observe(
      'oneviewExperienceSantanderEnabled',
    );
    const genericEnabled$ = this.featureFlags.observe(
      'oneviewExperienceGenericEnabled',
    );

    const mockedInputs = this.readMockedInputsFromUrl();
    if (mockedInputs) {
      const { permissions, isSantander } = mockedInputs;
      const natural = resolveOneViewExperienceVariant(permissions, isSantander);
      this.variant$ = combineLatest([santanderEnabled$, genericEnabled$]).pipe(
        map(([santanderEnabled, genericEnabled]) =>
          applyOneViewExperienceFlags(natural, { santanderEnabled, genericEnabled }),
        ),
        filter((variant): variant is OneViewExperienceVariant => variant !== null),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
      // eslint-disable-next-line no-console
      console.warn(
        '[OneViewExperienceVariantService] Mock ativo via ?mockOneView/?mockSantander. ' +
          `Inputs: showWalletOneView=${permissions.showWalletOneView}, ` +
          `isSantander=${isSantander} → variante natural=${natural ?? 'null (oculto)'}. ` +
          'Flags reais do Remote Config continuam sendo aplicadas.',
      );
      return;
    }

    const userTag$ = this.store
      .select((state) => state.userTag)
      .pipe(
        tap((userTagState) => this.dispatchUserTagIfIdle(userTagState)),
        map((userTagState) => isSantanderCustomer(userTagState?.tags)),
      );

    const oneViewPermissions$ = this.store
      .select((state) => state.oneViewPermissions)
      .pipe(
        tap((oneViewState) => this.dispatchOneViewIfIdle(oneViewState)),
      );

    this.variant$ = combineLatest([
      oneViewPermissions$,
      userTag$,
      santanderEnabled$,
      genericEnabled$,
    ]).pipe(
      map(([oneViewState, isSantander, santanderEnabled, genericEnabled]) => {
        const natural = resolveOneViewExperienceVariant(
          oneViewState?.permissions,
          isSantander,
        );
        return applyOneViewExperienceFlags(natural, {
          santanderEnabled,
          genericEnabled,
        });
      }),
      filter((variant): variant is OneViewExperienceVariant => variant !== null),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
  }

  private dispatchUserTagIfIdle(userTagState: UserTagState | undefined): void {
    if (!userTagState || userTagState.pending || userTagState.isLoaded) {
      return;
    }
    this.store.dispatch({ type: UserTagAction.getUserTag });
  }

  private dispatchOneViewIfIdle(oneViewState: OneViewPermissionsState | undefined): void {
    if (!oneViewState || oneViewState.pending || oneViewState.isLoaded) {
      return;
    }
    this.store.dispatch({ type: OneViewPermissionsAction.getOneViewPermissions });
  }

  /**
   * Lê `?forceVariant=new|old` da URL apenas em modo de desenvolvimento.
   * Retorna `null` em produção ou quando o parâmetro é ausente/inválido.
   */
  private readForcedVariantFromUrl(): OneViewExperienceVariant | null {
    if (!environment.allowDevOverrides || typeof window === 'undefined') {
      return null;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const raw = params.get('forceVariant');
      if (raw === 'new' || raw === 'old') {
        return raw;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Lê `?mockOneView` e `?mockSantander` da URL apenas em modo de
   * desenvolvimento. Basta um dos dois estar presente para ativar o modo mock;
   * params omitidos assumem `false`. Retorna `null` se nenhum estiver presente
   * ou em produção.
   */
  private readMockedInputsFromUrl(): {
    permissions: IOneViewPermissions;
    isSantander: boolean;
  } | null {
    if (!environment.allowDevOverrides || typeof window === 'undefined') {
      return null;
    }
    try {
      const params = new URLSearchParams(window.location.search);
      const hasAny =
        params.has('mockOneView') || params.has('mockSantander');
      if (!hasAny) {
        return null;
      }
      const toBool = (raw: string | null): boolean => raw === 'true';
      return {
        permissions: {
          showWalletOneView: toBool(params.get('mockOneView')),
        },
        isSantander: toBool(params.get('mockSantander')),
      };
    } catch {
      return null;
    }
  }
}
